import React from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { GraduationCap, LogOut, ChevronRight } from "lucide-react";

export default function DashboardLayout({ links = [], title = "Panel", testIdPrefix = "sidebar" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const logoSrc = settings?.logo_url 
    ? (settings.logo_url.startsWith('http') ? settings.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${settings.logo_url}`)
    : "/logo.png";
    
  return (
    <div className="min-h-screen bg-muted/30 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card">
        <Link to="/" className="flex items-center gap-2 h-16 px-6 border-b border-border">
          {settings?.favicon_url && (
            <img src={settings.favicon_url.startsWith('http') ? settings.favicon_url : `${process.env.REACT_APP_BACKEND_URL || ''}${settings.favicon_url}`} alt="Favicon" className="h-6 w-6 object-contain" />
          )}
          <img src={logoSrc} alt="Sano Student Logo" className="h-8 object-contain" />
          <div className="leading-none ml-2">
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{title}</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`${testIdPrefix}-link-${l.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              {l.icon && <l.icon className="h-4 w-4" />}
              <span>{l.label}</span>
              <ChevronRight className="ml-auto h-3 w-3 opacity-40" />
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-muted-foreground truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{user?.role}</div>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid={`${testIdPrefix}-logout-btn`}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Çıkış Yap
          </button>
        </div>
      </aside>
      {/* Mobile top nav */}
      <div className="flex-1 flex flex-col">
        <div className="lg:hidden flex items-center justify-between h-14 px-4 border-b border-border bg-card">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <GraduationCap className="h-4 w-4" />
            </div>
            <span className="font-display font-bold">SanoStudent · {title}</span>
          </Link>
          <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-muted-foreground hover:text-destructive">
            <LogOut className="h-4 w-4 inline" />
          </button>
        </div>
        {/* Mobile secondary nav */}
        <div className="lg:hidden overflow-x-auto border-b border-border bg-card">
          <div className="flex gap-1 p-2 min-w-max">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground bg-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
        <main className="flex-1 p-4 md:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
